import { CircleAlert, CircleCheck, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MessengerPageSummary } from '@/Config/api/queryClient';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

interface MessengerPendingPagesPanelProps {
  pages: MessengerPageSummary[];
  onConfirm: (page: MessengerPageSummary) => void;
  onConnectDifferentAccount: () => void;
  onCheckConnection: () => void;
  onCancel?: () => void;
  confirmingPageId: string | null;
  isCancelling: boolean;
  isConnecting: boolean;
  isCheckingConnection: boolean;
  showCheckConnection: boolean;
  isConnected: boolean;
  errorMessage: string | null;
}

export const MessengerPendingPagesPanel = ({
  pages,
  onConfirm,
  onConnectDifferentAccount,
  onCheckConnection,
  onCancel,
  confirmingPageId,
  isCancelling,
  isConnecting,
  isCheckingConnection,
  showCheckConnection,
  isConnected,
  errorMessage,
}: MessengerPendingPagesPanelProps) => {
  const { t } = useTranslation(['integration']);
  const singlePage = pages.length === 1 ? pages[0] : null;
  const isSelectionPending = confirmingPageId !== null;
  const titleKey = singlePage
    ? isConnected
      ? 'integration:messenger.select_page.single_title_connected'
      : 'integration:messenger.select_page.single_title'
    : 'integration:messenger.select_page.title';
  const descriptionKey = singlePage
    ? isConnected
      ? 'integration:messenger.select_page.single_description_connected'
      : 'integration:messenger.select_page.single_description'
    : 'integration:messenger.select_page.description';

  return (
    <section
      className="rounded-xl border border-border bg-card p-4"
      aria-labelledby="messenger-page-selection-title"
      data-testid="messenger-pending-panel"
    >
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success-muted text-success">
          <CircleCheck className="size-4" />
        </div>
        <div className="min-w-0">
          <h3 id="messenger-page-selection-title" className="text-sm font-medium text-foreground">
            {t(titleKey)}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{t(descriptionKey)}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {isConnecting ? (
          <div
            className="flex flex-col gap-2 rounded-lg bg-muted p-3"
            data-testid="messenger-pending-locked-hint"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Spinner className="size-4 shrink-0" />
              <span>{t('integration:messenger.select_page.confirm_locked_hint')}</span>
            </div>
            {showCheckConnection ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={onCheckConnection}
                loading={isCheckingConnection}
                data-testid="messenger-connect-waiting-dismiss"
              >
                <RefreshCw data-icon="inline-start" />
                {t('integration:messenger.select_page.waiting_dismiss')}
              </Button>
            ) : null}
          </div>
        ) : null}

        {errorMessage ? (
          <Alert variant="destructive" data-testid="messenger-page-selection-error">
            <CircleAlert className="size-4 text-destructive" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        {singlePage ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3"
            data-testid="messenger-page-selection-list"
          >
            <div className="min-w-0">
              <p
                className="truncate text-sm font-medium text-foreground"
                data-testid={`messenger-page-option-name-${singlePage.page_id}`}
              >
                {singlePage.page_name || singlePage.page_id}
              </p>
              <p className="truncate text-xs text-muted-foreground">{singlePage.page_id}</p>
            </div>
            <Button
              type="button"
              variant={singlePage.is_connected ? 'secondary' : 'dark'}
              size="sm"
              className="w-full min-w-0"
              onClick={() => onConfirm(singlePage)}
              disabled={confirmingPageId !== null || isConnecting}
              loading={confirmingPageId === singlePage.page_id}
              data-testid={`messenger-page-confirm-${singlePage.page_id}`}
            >
              <span className="truncate">
                {singlePage.is_connected
                  ? t('integration:messenger.select_page.keep')
                  : t('integration:messenger.select_page.confirm_named', {
                      name: singlePage.page_name || singlePage.page_id,
                    })}
              </span>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="messenger-page-selection-list">
            {pages.map((page) => (
              <div
                key={page.page_id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3"
                data-testid={`messenger-page-option-${page.page_id}`}
              >
                <div className="min-w-0">
                  <p
                    className="truncate text-sm font-medium text-foreground"
                    data-testid={`messenger-page-option-name-${page.page_id}`}
                  >
                    {page.page_name || page.page_id}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{page.page_id}</p>
                </div>
                <Button
                  type="button"
                  variant={page.is_connected ? 'secondary' : 'dark'}
                  size="sm"
                  className="shrink-0 aria-disabled:pointer-events-none"
                  onClick={() => {
                    if (!isSelectionPending) onConfirm(page);
                  }}
                  disabled={isConnecting}
                  aria-disabled={isSelectionPending || undefined}
                  loading={confirmingPageId === page.page_id}
                  data-testid={`messenger-page-confirm-${page.page_id}`}
                >
                  {page.is_connected
                    ? t('integration:messenger.select_page.keep')
                    : t('integration:messenger.select_page.confirm')}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={onConnectDifferentAccount}
            disabled={isConnecting}
            data-testid="messenger-pending-different-account"
          >
            <RefreshCw data-icon="inline-start" />
            {t('integration:messenger.select_page.different_account')}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={onCancel}
              disabled={isConnecting}
              loading={isCancelling}
              data-testid="messenger-pending-cancel-button"
            >
              <X data-icon="inline-start" />
              {t('integration:messenger.select_page.cancel')}
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
};
